import React from 'react';

import { EditIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { toast } from 'react-toastify';

import { EditDepartmentInfoForm } from '@vlabs/monokit/entities/departments';
import type { EditDepartmentInfoFormValues } from '@vlabs/monokit/entities/departments';
import { useGetDepartment, usePatchDepartment } from '@vlabs/api/artemy/client';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useEditDepartmentInfoTranslation } from './i18n';
import type { EditDepartmentInfoProps } from './types';

interface EditDepartmentInfoButtonProps extends Omit<ButtonProps, 'onClick'>, EditDepartmentInfoProps { }

const displayName = 'EditDepartmentInfoButton';
const testid = buildTestid(displayName);

function EditDepartmentInfoButton({
  departmentId,
  onEditDepartmentInfo = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: EditDepartmentInfoButtonProps): React.JSX.Element {
  const { t } = useEditDepartmentInfoTranslation();
  const modal = useModal({ size: 'small' });

  const getDepartment = useGetDepartment(departmentId);
  const patchDepartment = usePatchDepartment({
    mutation: {
      onSuccess: () => {
        modal.close();
        toast.success(t('toast_success'));
        onEditDepartmentInfo?.();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const defaultValues: EditDepartmentInfoFormValues = {
    name: getDepartment.data?.name ?? '',
    description: getDepartment.data?.description ?? '',
  };

  const handleSubmit = (values: EditDepartmentInfoFormValues): void => {
    patchDepartment.mutate({ departmentId, data: values });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <EditDepartmentInfoForm
          defaultValues={defaultValues}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<EditIcon />}
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      >
        {t('edit_button')}
      </Button>
    </React.Fragment>
  );
}

EditDepartmentInfoButton.displayName = displayName;

export { EditDepartmentInfoButton };
